import React from 'react';
import { Camera, Upload, Trash2, X } from 'lucide-react';
import type { RoomPhoto } from '../types';
import { deletePhotoFromSupabase } from '../utils/supabaseStorage';

interface SectionPhotoGalleryProps {
  photos: RoomPhoto[];
  onCameraClick: () => void;
  onUploadClick: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDeletePhoto: (photoId: string) => void;
  isProcessing?: boolean;
  sectionLabel?: string;
}

const SectionPhotoGallery: React.FC<SectionPhotoGalleryProps> = ({
  photos,
  onCameraClick,
  onUploadClick,
  onDeletePhoto,
  isProcessing = false,
  sectionLabel = 'Photos'
}) => {
  const [selectedPhotoIndex, setSelectedPhotoIndex] = React.useState<number | null>(null);
  
  const validPhotos = photos.filter(p => p && !p.uploading && (p.url || p.localPath));

  const handleDeletePhoto = async (photo: RoomPhoto, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (!confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      // Delete from Supabase if it has a fileName
      if (photo.fileName) {
        await deletePhotoFromSupabase(photo.fileName, 'inventory-photos');
      }
      
      // Call parent handler to remove from state
      onDeletePhoto(photo.id);
    } catch (error) {
      console.error('Error deleting photo:', error);
      alert('Failed to delete photo. Please try again.');
    }
  };

  const openLightbox = (index: number) => {
    setSelectedPhotoIndex(index);
  };

  const closeLightbox = () => {
    setSelectedPhotoIndex(null);
  };

  const navigateLightbox = (direction: 'prev' | 'next') => {
    if (selectedPhotoIndex === null) return;
    
    if (direction === 'prev') {
      setSelectedPhotoIndex(selectedPhotoIndex > 0 ? selectedPhotoIndex - 1 : validPhotos.length - 1);
    } else {
      setSelectedPhotoIndex(selectedPhotoIndex < validPhotos.length - 1 ? selectedPhotoIndex + 1 : 0);
    }
  };

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="mt-4">
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onUploadClick}
        className="hidden"
      />
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-gray-700">{sectionLabel}</h4>
        <div className="flex gap-2">
          <button
            onClick={onCameraClick}
            disabled={isProcessing}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg hover:from-blue-600 hover:to-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            <Camera size={14} />
            Camera
          </button>
          <button
            onClick={handleUploadClick}
            disabled={isProcessing}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-lg hover:from-emerald-600 hover:to-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
          >
            <Upload size={14} />
            Upload
          </button>
        </div>
      </div>

      {/* Upload Processing Indicator */}
      {isProcessing && (
        <div className="mb-3 bg-blue-50 border-2 border-blue-200 rounded-lg p-3">
          <div className="flex items-center gap-2">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
            <span className="text-sm font-medium text-blue-800">Processing photos...</span>
          </div>
        </div>
      )}

      {validPhotos.length > 0 ? (
        <div className="relative">
          {/* Horizontal Scrollable Gallery */}
          <div className="flex gap-3 overflow-x-auto pb-2 snap-x snap-mandatory scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100">
            {validPhotos.map((photo, index) => {
              const imageUrl = photo.url || photo.localPath || '';
              
              return (
                <div
                  key={photo.id}
                  className="relative group flex-shrink-0 w-32 h-32 rounded-xl overflow-hidden border-2 border-slate-200 hover:border-blue-400 transition-all cursor-pointer snap-start shadow-sm hover:shadow-md"
                  onClick={() => openLightbox(index)}
                >
                  <img
                    src={imageUrl}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover"
                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                  />
                  <button
                    onClick={(e) => handleDeletePhoto(photo, e)}
                    className="absolute top-1.5 right-1.5 p-1.5 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600 shadow-md"
                    aria-label="Delete photo"
                  >
                    <Trash2 size={14} />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                    <span className="text-white text-xs font-bold drop-shadow-md">{index + 1}</span>
                  </div>
                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-600/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"></div>
                </div>
              );
            })}
          </div>
          {/* Scroll hint */}
          {validPhotos.length > 4 && (
            <div className="text-center mt-2">
              <span className="text-xs text-slate-500 italic">← Scroll for more photos →</span>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-6 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
          <Camera size={24} className="mx-auto text-slate-400 mb-2" />
          <p className="text-slate-500 text-sm font-medium">No photos added yet</p>
          <p className="text-slate-400 text-xs mt-1">Tap Camera or Upload to add photos</p>
        </div>
      )}

      {/* Modern Lightbox Modal */}
      {selectedPhotoIndex !== null && validPhotos[selectedPhotoIndex] && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 backdrop-blur-sm animate-in fade-in duration-200"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 p-3 text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-10 shadow-lg backdrop-blur-sm"
            aria-label="Close"
          >
            <X size={24} />
          </button>
          
          {/* Previous Button */}
          {validPhotos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox('prev'); }}
              className="absolute left-6 p-3 text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-10 shadow-lg backdrop-blur-sm"
              aria-label="Previous"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          
          {/* Next Button */}
          {validPhotos.length > 1 && (
            <button
              onClick={(e) => { e.stopPropagation(); navigateLightbox('next'); }}
              className="absolute right-6 p-3 text-white bg-white/10 hover:bg-white/20 rounded-full transition-all z-10 shadow-lg backdrop-blur-sm"
              aria-label="Next"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}
          
          {/* Image Container with proper boundaries */}
          <div className="max-w-[95vw] max-h-[90vh] flex items-center justify-center p-4">
            <img
              src={validPhotos[selectedPhotoIndex].url || validPhotos[selectedPhotoIndex].localPath || ''}
              alt={`Photo ${selectedPhotoIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
              style={{ maxWidth: '100%', maxHeight: '85vh' }}
            />
          </div>
          
          {/* Photo Counter */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-slate-900/90 to-slate-800/90 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg border border-white/10">
            <span className="text-white text-sm font-semibold">
              {selectedPhotoIndex + 1} / {validPhotos.length}
            </span>
          </div>
          
          {/* Delete Button in Lightbox */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDeletePhoto(validPhotos[selectedPhotoIndex], e);
              if (selectedPhotoIndex >= validPhotos.length - 1) {
                setSelectedPhotoIndex(Math.max(0, selectedPhotoIndex - 1));
              }
            }}
            className="absolute top-6 left-6 p-3 text-white bg-red-500/80 hover:bg-red-600 rounded-full transition-all z-10 shadow-lg backdrop-blur-sm"
            aria-label="Delete photo"
          >
            <Trash2 size={20} />
          </button>
        </div>
      )}
    </div>
  );
};

export default SectionPhotoGallery;
